import classes from './Details.module.sass'
import { OrderInterface } from '../../../../../common/types/interfaces'
import {
  getDeliveryTypeText,
  getOrderStatusColor,
  getOrderStatusText,
  getPaymentMethodText,
  getPaymentStatusColor,
  getPaymentStatusText,
  getDeliveryStatusText,
  getTaskStatusText,
  getTaskStatusColor,
  getTaskTypeText,
} from '../../../../../common/utils/functions/enumToText'
import { DeliveryService, TaskStatus } from '../../../../../common/types/enums'
import { format } from 'date-fns'
import { formatPhoneNumber } from '../../../../../common/utils/functions/formatPhoneNumber'
import { IconButton } from '../../../../../common/components/iconButton/IconButton'
import print from '../../../../../assets/images/icons/print.svg'
import edit from '../../../../../assets/images/icons/edit.svg'
import { usePrint } from '../../../../../common/hooks/usePrint'
import QRCode from 'react-qr-code'

type Props = {
  el?: OrderInterface
  onEdit?: (el: OrderInterface) => void
}

export const Details = ({ el, onEdit }: Props) => {
  const { reactToPrintFn, contentRef } = usePrint()

  if (el)
    return (
      <div>
        <div className={classes.container}>
          <div className={classes.content} ref={contentRef}>
            <div className={classes.title}>клиент</div>
            <div className={classes.item}>
              <div className={classes.label}>Телефон </div>
              <div className={classes.value}>
                {formatPhoneNumber(el.phone_number ? el.phone_number : el?.user?.phone_number)}
              </div>
            </div>

            <div className={classes.title}>детали заказа</div>
            <div className={classes.row}>
              <div className={classes.id}>
                <div className={classes.label}>id</div>
                <div className={classes.value}>{el.id}</div>
              </div>

              {el.createdAt && (
                <div className={classes.item}>
                  <div className={classes.label}>Создан</div>
                  <div className={classes.value}>
                    {format(new Date(el.createdAt), 'dd.MM.yyyy HH:mm')}
                  </div>
                </div>
              )}
              {el.updatedAt && (
                <div className={classes.item}>
                  <div className={classes.label}>Обновлен</div>
                  <div className={classes.value}>
                    {format(new Date(el.updatedAt), 'dd.MM.yyyy HH:mm')}
                  </div>
                </div>
              )}
            </div>

            <div className={classes.row}>
              <div className={classes.item}>
                <div className={classes.label}>Тип доставки</div>
                <div className={classes.value}>{getDeliveryTypeText(el.deliveryType)}</div>
              </div>
              <div className={classes.item}>
                <div className={classes.label}>Тип оплаты </div>
                <div className={classes.value}>{getPaymentMethodText(el.paymentMethod)}</div>
              </div>
            </div>

            <div className={classes.row}>
              <div className={classes.item}>
                <div className={classes.label}>
                  {el.deliveryType === 1 ? 'Время самовывоза' : 'Время доставки'}
                </div>
                <div className={classes.value}>
                  {el.desiredTime
                    ? format(new Date(el.desiredTime), 'dd.MM.yyyy HH:mm')
                    : 'Ближайшее время'}
                </div>
              </div>
            </div>

            <div className={classes.row}>
              <div className={classes.item}>
                <div className={classes.label}>Оплачен</div>
                <div
                  className={classes.value}
                  style={{ color: getPaymentStatusColor(el.paymentStatus) }}
                >
                  {getPaymentStatusText(el.paymentStatus)}
                </div>
              </div>
              <div className={classes.item}>
                <div className={classes.label}>Статус</div>
                <div className={classes.value} style={{ color: getOrderStatusColor(el.status) }}>
                  {getOrderStatusText(el.status)}
                </div>
              </div>
            </div>

            {el.fullAddress && (
              <>
                <div className={classes.row}>
                  <div className={classes.item}>
                    <div className={classes.label}>Адрес</div>
                    <div className={classes.value}>{el.fullAddress.address.value}</div>
                  </div>
                  <div className={classes.item}>
                    <div className={classes.label}>Город</div>
                    <div className={classes.value}>{el.fullAddress.city.value}</div>
                  </div>
                </div>
              </>
            )}
            {el.baseAddress && (
              <>
                <div className={classes.row}>
                  <div className={classes.item}>
                    <div className={classes.label}>Адрес</div>
                    <div className={classes.value}>{el.baseAddress.address.value}</div>
                  </div>
                  <div className={classes.item}>
                    <div className={classes.label}>Город</div>
                    <div className={classes.value}>{el.baseAddress.city.value}</div>
                  </div>
                </div>
              </>
            )}

            {el.store && (
              <>
                <div className={classes.item}>
                  <div className={classes.label}>Торговая точка</div>
                  <div className={classes.value}>{el.store.name}</div>
                </div>
                <div className={classes.row}>
                  <div className={classes.item}>
                    <div className={classes.label}>Адрес</div>
                    <div className={classes.value}>{el.store.address.value}</div>
                  </div>
                  <div className={classes.item}>
                    <div className={classes.label}>Город</div>
                    <div className={classes.value}>{el.store.city.value}</div>
                  </div>
                </div>
              </>
            )}

            {/* ❌ Задания (read-only, все, активные сверху) */}
            {el.tasks && el.tasks.length > 0 && (
              <>
                <div className={classes.title}>задания ({el.tasks.length})</div>
                {[...el.tasks]
                  .sort((a, b) => {
                    const isActiveA =
                      a.status !== TaskStatus.CANCELLED && a.status !== TaskStatus.DELIVERED
                    const isActiveB =
                      b.status !== TaskStatus.CANCELLED && b.status !== TaskStatus.DELIVERED
                    if (isActiveA && !isActiveB) return -1
                    if (!isActiveA && isActiveB) return 1
                    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                  })
                  .map((task) => {
                    const isActive =
                      task.status !== TaskStatus.CANCELLED && task.status !== TaskStatus.DELIVERED
                    return (
                      <div key={task.id} className={classes.row}>
                        <div className={classes.id}>
                          <div className={classes.label}>id</div>
                          <div className={classes.value}>{task.id}</div>
                        </div>
                        <div className={classes.item}>
                          <div className={classes.label}>Статус</div>
                          <div
                            className={classes.value}
                            style={{
                              color: getTaskStatusColor(task.status),
                              fontWeight: isActive ? 'bold' : 'normal',
                            }}
                          >
                            {getTaskStatusText(task.status)}
                          </div>
                        </div>
                        <div className={classes.item}>
                          <div className={classes.label}>Тип</div>
                          <div className={classes.value}>{getTaskTypeText(task.type)}</div>
                        </div>
                        {task.executor && (
                          <div className={classes.item}>
                            <div className={classes.label}>Исполнитель</div>
                            <div className={classes.value}>{task.executor.name}</div>
                          </div>
                        )}
                      </div>
                    )
                  })}
              </>
            )}

            {el.deliveryService === DeliveryService.CDEK &&
              el.delivery &&
              el.delivery.length > 0 && (
                <>
                  <div className={classes.title}>доставка СДЭК</div>
                  <div className={classes.row}>
                    <div className={classes.item}>
                      <div className={classes.label}>Статус</div>
                      <div className={classes.value}>
                        {getDeliveryStatusText(el.delivery[0].status)}
                      </div>
                    </div>
                    {el.delivery[0].tariffName && (
                      <div className={classes.item}>
                        <div className={classes.label}>Тариф</div>
                        <div className={classes.value}>{el.delivery[0].tariffName}</div>
                      </div>
                    )}
                  </div>
                  {el.delivery[0].pickupPointName && (
                    <div className={classes.row}>
                      <div className={classes.item}>
                        <div className={classes.label}>Пункт выдачи</div>
                        <div className={classes.value}>{el.delivery[0].pickupPointName}</div>
                      </div>
                    </div>
                  )}
                  {el.delivery[0].pickupPointAddress && (
                    <div className={classes.row}>
                      <div className={classes.item}>
                        <div className={classes.label}>Адрес ПВЗ</div>
                        <div className={classes.value}>{el.delivery[0].pickupPointAddress}</div>
                      </div>
                    </div>
                  )}
                  {el.delivery[0].deliveryOrderId && (
                    <div className={classes.row}>
                      <div className={classes.item}>
                        <div className={classes.label}>Трек-номер</div>
                        <div className={classes.value}>{el.delivery[0].deliveryOrderId}</div>
                      </div>
                    </div>
                  )}
                </>
              )}

            <div className={classes.item}>
              <div className={classes.label}>Стоимость доставки</div>
              <div className={classes.value}>{el.deliveryFee}</div>
            </div>

            <div className={classes.row}>
              <div className={classes.item}>
                <div className={classes.label}>Позиций в зваказе</div>
                <div className={classes.value}>{el.items?.length}</div>
              </div>
              <div className={classes.item}>
                <div className={classes.label}>Сумма</div>
                <div className={classes.value}>{el.total.toLocaleString('ru-RU')} ₽</div>
              </div>
            </div>

            {el.description && (
              <div className={classes.item}>
                <div className={classes.label}>Комментарий клиента</div>
                <div className={classes.value}>{el.description}</div>
              </div>
            )}

            {el.items && (
              <>
                <div className={classes.title}>состав заказа</div>
                {el.items.map((el) => (
                  <div key={el.id}>
                    <div className={classes.row}>
                      <div className={classes.name}>
                        <div className={classes.label}>Наименование</div>
                        <div className={classes.itemValue}>{el.name}</div>
                      </div>
                      <div className={classes.item}>
                        <div className={classes.label}>Количество</div>
                        <div className={classes.itemValue}>{el.quantity}</div>
                      </div>
                      <div className={classes.item}>
                        <div className={classes.label}> Цена</div>
                        <div className={classes.itemValue}>
                          {el.price.toLocaleString('ru-RU')} ₽
                        </div>
                      </div>
                      <div className={classes.item}>
                        <div className={classes.label}> Сумма</div>
                        <div className={classes.itemValue}>
                          {el.total.toLocaleString('ru-RU')} ₽
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
            <div className={classes.qrContainer}>
              {el?.uuid && <QRCode value={el.uuid} className={classes.qr} />}
            </div>
          </div>
        </div>
        <div className={classes.button}>
          {onEdit && (
            <IconButton small display onClick={() => onEdit(el)} icon={edit} alt="Редактировать" />
          )}
          <IconButton small display onClick={reactToPrintFn} icon={print} alt="Печать" />
        </div>
      </div>
    )
}
